import { colors, cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { cssThemedValue } from '@creditornot/cb-sc-utils';
import { DateTime } from 'luxon';
import React from 'react';
import { useIntl } from 'react-intl';
import { Payload } from 'recharts/types/component/DefaultTooltipContent';
import styled from 'styled-components';
import { Overwrite } from 'utility-types/dist/mapped-types';

import { SIMPLE_DATE_FORMAT } from 'apis/merchant-gateway/insights';
import { Typography as T } from 'common/typography';

import { FormatterFn, SummaryValue } from './types';

type CustomTooltipProps = {
  active?: boolean;
  /** Payload is { name: string; value: number, payload?: { summaryValue: number }, etc... } */
  payload?: Overwrite<Payload<number, string>, { payload?: SummaryValue }>[];
  label?: string;
  summaryKey?: string;
  ignoreKeysForSummaryCalc?: string[];
  /** if provided, will be used to render the tooltip above the bars row; used for horizontal bar charts */
  rowHeight?: number;
  summaryCurrency?: Currency;
  formatter?: FormatterFn;
  isReverse?: boolean;
};

type TooltipRowProps = {
  title?: string;
  content?: string;
  isEmphasized?: boolean;
};

const CustomToolTipRoot = styled.div<{ rowHeight?: number }>`
  background-color: ${cbSemanticCssVars.textPrimary};
  border-radius: 12px;
  color: ${cbSemanticCssVars.surfaceMain};
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 12px;
  min-width: 194px;
  ${props => props.rowHeight && `transform: translate(0, calc(-100% - 16px - ${props.rowHeight / 2}px));`}
`;

const TooltipTitle = styled.small`
  font-weight: 600;
`;

const TooltipRowRoot = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
`;

const TooltipContent = styled.small<{ isEmphasized?: boolean }>`
  ${({ isEmphasized }) => isEmphasized && 'font-weight: 500;'};
  color: ${({ isEmphasized }) =>
    isEmphasized ? cbSemanticCssVars.surfaceMain : cssThemedValue({ light: colors.salt64, dark: colors.pepper64 })};
`;

const TooltipRow = ({ title, content, isEmphasized }: TooltipRowProps) => (
  <TooltipRowRoot>
    <T.Small>{title}</T.Small>
    <TooltipContent isEmphasized={isEmphasized}>{content}</TooltipContent>
  </TooltipRowRoot>
);

export const CustomTooltip = ({
  active,
  label,
  payload,
  summaryKey,
  ignoreKeysForSummaryCalc = [],
  rowHeight,
  summaryCurrency,
  formatter,
  isReverse,
}: CustomTooltipProps) => {
  const intl = useIntl();

  const summaryValue = payload?.find(item => item.payload?.summaryValue != null)?.payload?.summaryValue;

  const formatLabel = (label: string) => {
    const date = DateTime.fromFormat(label, SIMPLE_DATE_FORMAT);
    return date.isValid ? date.setLocale(intl.locale).toLocaleString(DateTime.DATE_HUGE) : label;
  };

  const formatValue = (amount: number, currency?: Currency) =>
    formatter
      ? formatter(amount)
      : intl.formatNumber(amount, currency ? { currency, style: 'currency' } : { maximumFractionDigits: 2 });

  if (active && payload) {
    const finalPayload = ignoreKeysForSummaryCalc.length
      ? payload.filter(item => !ignoreKeysForSummaryCalc.includes(item?.dataKey?.toString() ?? ''))
      : payload;

    const derivedSummaryValue =
      finalPayload.map(item => item.value).reduce((acc: number, curr = 0) => acc + curr, 0) ?? 0;

    return (
      <CustomToolTipRoot rowHeight={rowHeight}>
        {label && <TooltipTitle>{formatLabel(label)}</TooltipTitle>}
        {summaryKey && (
          <TooltipRow
            title={intl.formatMessage({ id: summaryKey })}
            content={formatValue(summaryValue ?? derivedSummaryValue, summaryCurrency)}
            isEmphasized
          />
        )}
        {payload.length > 1 || !summaryKey
          ? (isReverse ? payload.reverse() : payload).map(({ name, value, unit: barCurrency }) => (
              <TooltipRow
                key={name}
                title={name}
                content={formatValue(value ?? 0, barCurrency as Currency | undefined)}
              />
            ))
          : null}
      </CustomToolTipRoot>
    );
  }
  return null;
};
